import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useAgentTeamStore } from '../../store/agentTeamStore';
import { useThemeStore } from '../../store/themeStore';
import type { ConversationRole } from '../../types/agentTeam';
import {
  Conversation,
  ConversationMessages,
  ConversationInput,
} from '../ai-elements';
import {
  toAIElementsMessages,
  streamingToAIElements,
} from '../../adapters/agentTeamAdapter';
import { AgentTeamHeader } from './AgentTeamHeader';
import { AgentTeamSessionList } from './AgentTeamSessionList';
import { AgentTeamMessage } from './AgentTeamMessage';
import { WorkerDynamicIsland } from './WorkerDynamicIsland';
import { AIGeneratedNotice } from '../ui/AIGeneratedNotice';
import { QuestionPrompt } from '../chat/QuestionPrompt';
import type { TaskDispatchItem, WorkflowStageState } from '../../types/agentTeam';
import { getAgentTeamApi } from '../../services/agentTeamApi';
import { AbortConfirmDialog } from '../ui/AbortConfirmDialog';

const MAX_MESSAGE_LENGTH = 10240;

const STAGE_STATUS_STYLES: Record<string, { dot: string; text: string; bar: string }> = {
  pending: { dot: 'bg-gray-300', text: 'text-gray-400', bar: 'bg-gray-200' },
  running: { dot: 'bg-blue-500 animate-pulse', text: 'text-blue-700', bar: 'bg-blue-500' },
  completed: { dot: 'bg-green-500', text: 'text-green-700', bar: 'bg-green-500' },
  failed: { dot: 'bg-red-500', text: 'text-red-700', bar: 'bg-red-500' },
  skipped: { dot: 'bg-gray-400', text: 'text-gray-500', bar: 'bg-gray-400' },
};

const WorkflowProgressBar: React.FC<{ stages: WorkflowStageState[] }> = ({ stages }) => {
  if (!stages || stages.length === 0) return null;

  const completedCount = stages.filter(s => s.status === 'completed').length;
  const progressPct = stages.length > 0 ? (completedCount / stages.length) * 100 : 0;

  return (
    <div className="px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-purple-100 flex-shrink-0">
      <div className="flex items-center gap-1 mb-1.5">
        <span className="text-xs font-medium text-purple-800">工作流进度</span>
        <span className="text-xs text-purple-600">{completedCount}/{stages.length}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
        <div
          className="bg-purple-500 h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <div className="flex items-center gap-2">
        {stages.map((s, idx) => {
          const styles = STAGE_STATUS_STYLES[s.status] || STAGE_STATUS_STYLES.pending;
          return (
            <React.Fragment key={s.stage}>
              {idx > 0 && <div className="flex-1 h-px bg-gray-300 min-w-[8px]" />}
              <div className="flex items-center gap-1 flex-shrink-0">
                <div className={`w-2 h-2 rounded-full ${styles.dot}`} />
                <span className={`text-xs ${styles.text}`}>{s.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

interface AgentTeamPanelProps {
  workspaceId: number;
}

export const AgentTeamPanel: React.FC<AgentTeamPanelProps> = ({ workspaceId }) => {
  const {
    session,
    error,
    processing,
    streaming,
    pendingQuestion,
    getConversations,
    getStages,
    initSession,
    startSSEStream,
    submitQuestionAnswer,
    dismissQuestion,
    setError,
  } = useAgentTeamStore();

  const [inputValue, setInputValue] = useState('');
  const [taskList, setTaskList] = useState<TaskDispatchItem[]>([]);
  const [showSessionList, setShowSessionList] = useState(false);
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);
  const [abortAnimating, setAbortAnimating] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const stages = getStages();
  const { resolvedTheme } = useThemeStore();

  const fetchTasks = useCallback(async () => {
    if (!session) return;
    try {
      const api = getAgentTeamApi();
      const res = await api.getSessionTasks(session.id);
      setTaskList((res as any).dispatches || []);
    } catch {
      // ignore
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchTasks();
    }
  }, [session, fetchTasks]);

  useEffect(() => {
    if (!streaming.isStreaming && session && taskList.length > 0) {
      fetchTasks();
    }
  }, [streaming.isStreaming, session, fetchTasks, taskList.length]);

  useEffect(() => {
    return () => {
      const controller = useAgentTeamStore.getState().sseAbortController;
      if (controller) {
        controller.abort();
      }
      useAgentTeamStore.setState({
        sseConnected: false,
        sseAbortController: null,
        processing: false,
      });
    };
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  const handleSendMessage = useCallback(async () => {
    const content = inputValue.trim();
    if (!content || content.length > MAX_MESSAGE_LENGTH || processing || streaming.isStreaming) {
      return;
    }

    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setError(null);

    try {
      if (!session) {
        await initSession(workspaceId, content);
      } else {
        await startSSEStream(content);
      }
    } catch (err) {
      console.error('[AgentTeamPanel] 发送消息失败:', err);
    }
  }, [inputValue, processing, streaming.isStreaming, session, workspaceId, initSession, startSSEStream, setError]);

  const handleAbort = useCallback(() => {
    setShowAbortConfirm(true);
  }, []);

  const confirmAbort = useCallback(async () => {
    setShowAbortConfirm(false);
    setAbortAnimating(true);

    const currentSession = useAgentTeamStore.getState().session;
    if (currentSession?.id) {
      useAgentTeamStore.getState().cancelSSEStream();
      try {
        await useAgentTeamStore.getState().cancelExecution(currentSession.id);
      } catch (err) {
        console.error('[AgentTeamPanel] 取消执行失败:', err);
      }
    }

    setTimeout(() => setAbortAnimating(false), 1500);
  }, []);

  const handleSendToQueue = useCallback(async () => {
    const content = inputValue.trim();
    if (!content || !session?.id) return;

    try {
      await useAgentTeamStore.getState().sendMessageToQueue(session.id, content);
      setInputValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      console.error('[AgentTeamPanel] 发送消息到队列失败:', err);
    }
  }, [inputValue, session]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    adjustTextareaHeight();
  }, [adjustTextareaHeight]);

  const conversations = getConversations();
  const messages = toAIElementsMessages(conversations);
  const streamingMessage = streaming.isStreaming
    ? streamingToAIElements(
        streaming.streamingContent,
        (streaming.streamingRole || 'planner') as ConversationRole,
        streaming.streamingSenderName,
        streaming.parts,
      )
    : null;

  const isDisabled = processing || streaming.isStreaming;
  const charCount = inputValue.length;
  const showCharWarning = charCount > MAX_MESSAGE_LENGTH * 0.9;

  return (
    <Conversation className={resolvedTheme === 'dark' ? 'dark' : ''}>
      <AgentTeamHeader
        showSessionList={showSessionList}
        onToggleSessionList={() => setShowSessionList(prev => !prev)}
      />

      <AgentTeamSessionList
        workspaceId={workspaceId}
        isOpen={showSessionList}
        onClose={() => setShowSessionList(false)}
      />

      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex-shrink-0">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {(streaming.isStreaming || stages.length > 0) && (
        <WorkflowProgressBar stages={stages} />
      )}

      <ConversationMessages autoScroll>
        {messages.length === 0 && !streamingMessage ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 px-4">
            <svg className="w-16 h-16 mb-4 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-center text-sm mb-2">与智能体团队协作</p>
            <p className="text-center text-xs text-gray-400">
              描述您的需求，规划者将分析并协调团队完成任务
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <AgentTeamMessage key={msg.id} message={msg} />
            ))}
            {streamingMessage && (
              <AgentTeamMessage key="streaming" message={streamingMessage} />
            )}
          </>
        )}
        <WorkerDynamicIsland />
      </ConversationMessages>

      {pendingQuestion && (
        <div className="flex-shrink-0 px-4 py-2 border-t border-border/50">
          <QuestionPrompt
            question={pendingQuestion}
            onSubmit={(answers) => submitQuestionAnswer(pendingQuestion.question_id, answers)}
            onDismiss={() => dismissQuestion(pendingQuestion.question_id)}
          />
        </div>
      )}

      {!pendingQuestion && (
        <ConversationInput>
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={isDisabled ? '任务执行中，可输入消息排队...' : '请输入消息，Shift+Enter换行'}
              rows={1}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 resize-none overflow-hidden min-h-[38px] max-h-[120px]"
            />
            {isDisabled && !inputValue.trim() ? (
              <button
                onClick={handleAbort}
                disabled={abortAnimating}
                className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors shrink-0 ${
                  abortAnimating
                    ? 'bg-green-500 cursor-not-allowed'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {abortAnimating ? '已中止' : '中止'}
              </button>
            ) : (
              <button
                onClick={isDisabled ? handleSendToQueue : handleSendMessage}
                disabled={!inputValue.trim() || (abortAnimating)}
                className="px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {isDisabled ? '排队发送' : '发送'}
              </button>
            )}
          </div>
          <div className="mt-1 flex items-center justify-end text-xs text-gray-400">
            {showCharWarning && (
              <span className={charCount > MAX_MESSAGE_LENGTH ? 'text-red-500' : 'text-amber-500'}>
                {charCount}/{MAX_MESSAGE_LENGTH}
              </span>
            )}
          </div>
        </ConversationInput>
      )}

      {showAbortConfirm && (
        <AbortConfirmDialog
          onConfirm={confirmAbort}
          onCancel={() => setShowAbortConfirm(false)}
        />
      )}

      <div className="text-center px-4 pb-1">
        <AIGeneratedNotice variant="block" className="py-0" />
      </div>
    </Conversation>
  );
};

export default AgentTeamPanel;
